import { useRef, useState } from 'react';

import { formatMoney } from '../../lib/money';
import { FinanceCoin } from './FinanceCoin';

export function FinanceBubble<Payload>({
  name,
  amount,
  caption,
  ariaLabel,
  color = '#35A9FF',
  icon = 'wallet',
  iconFallback = 'wallet',
  progress,
  danger = false,
  mutedAmount = false,
  dragPayload,
  canDrop,
  dropTargetId,
  dropTargetActive = false,
  onDragStart,
  onDragCancel,
  onDrop,
  onContextAction,
  onLongPress,
  onClick,
  formatAmount = formatMoney,
}: {
  name: string;
  amount: number;
  caption?: string;
  ariaLabel?: string;
  color?: string;
  icon?: string;
  iconFallback?: string;
  progress?: number;
  danger?: boolean;
  mutedAmount?: boolean;
  dragPayload?: Payload;
  canDrop: (payload: Payload, targetId: string) => boolean;
  dropTargetId?: string;
  dropTargetActive?: boolean;
  onDragStart?: (payload: Payload) => void;
  onDragCancel?: () => void;
  onDrop?: (payload: Payload, targetId: string) => void;
  onContextAction?: (payload: Payload) => void;
  onLongPress?: () => void;
  onClick?: () => void;
  formatAmount?: (amount: number) => string;
}) {
  const origin = useRef({ x: 0, y: 0 });
  const pointerStartedAt = useRef(0);
  const pointerType = useRef('');
  const moved = useRef(false);
  const longPressed = useRef(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragArmed, setDragArmed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const resetDrag = () => {
    moved.current = false;
    setDragArmed(false);
    setDragging(false);
    setDragOffset({ x: 0, y: 0 });
  };
  const finishDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (longPressTimer.current !== undefined) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = undefined;
    }
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    if (!dragPayload) {
      onClick?.();
      return;
    }
    if (!moved.current) {
      const wasQuickTap = event.timeStamp - pointerStartedAt.current < 350;
      onDragCancel?.();
      resetDrag();
      if (wasQuickTap) onClick?.();
      return;
    }
    event.currentTarget.style.pointerEvents = 'none';
    const target = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest<HTMLElement>('[data-drop-target]');
    event.currentTarget.style.pointerEvents = '';
    const targetId = target?.dataset.dropTarget;
    if (targetId && canDrop(dragPayload, targetId)) {
      navigator.vibrate?.(25);
      onDrop?.(dragPayload, targetId);
    } else {
      onDragCancel?.();
    }
    resetDrag();
  };
  return (
    <button
      type="button"
      className={`finance-bubble ${dragPayload ? 'draggable' : ''} ${danger ? 'danger' : ''} ${dragArmed ? 'drag-armed' : ''} ${dragging ? 'dragging' : ''} ${dropTargetActive ? 'drop-target' : ''}`}
      data-drop-target={dropTargetId}
      aria-label={ariaLabel ?? `${name}, ${formatAmount(amount)}${dragPayload ? '. Можно перетаскивать' : ''}`}
      onClick={dragPayload ? undefined : onClick}
      onContextMenu={event => {
        if (!dragPayload || !onContextAction) return;
        event.preventDefault();
        if (pointerType.current === 'touch') return;
        onDragCancel?.();
        resetDrag();
        onContextAction(dragPayload);
      }}
      onKeyDown={event => {
        if (
          dragPayload &&
          onContextAction &&
          event.shiftKey &&
          event.key === 'F10'
        ) {
          event.preventDefault();
          onContextAction(dragPayload);
          return;
        }
        if (
          dragPayload &&
          onClick &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          onClick();
        }
      }}
      onPointerDown={event => {
        if (!dragPayload && !onLongPress) return;
        pointerType.current = event.pointerType;
        origin.current = { x: event.clientX, y: event.clientY };
        pointerStartedAt.current = event.timeStamp;
        moved.current = false;
        longPressed.current = false;
        if (dragPayload) {
          event.preventDefault();
          setDragArmed(true);
          navigator.vibrate?.(8);
          onDragStart?.(dragPayload);
        } else if (onLongPress) {
          longPressTimer.current = setTimeout(() => {
            longPressed.current = true;
            navigator.vibrate?.(20);
            onLongPress();
          }, 550);
        }
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={event => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        const x = event.clientX - origin.current.x;
        const y = event.clientY - origin.current.y;
        if (!dragPayload) {
          if (Math.hypot(x, y) > 7 && longPressTimer.current !== undefined) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = undefined;
          }
          return;
        }
        event.preventDefault();
        if (!moved.current && Math.hypot(x, y) > 4) {
          moved.current = true;
          setDragging(true);
          navigator.vibrate?.(15);
        }
        if (moved.current) setDragOffset({ x, y });
      }}
      onPointerUp={finishDrag}
      onPointerCancel={() => {
        if (longPressTimer.current !== undefined) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = undefined;
        }
        longPressed.current = false;
        onDragCancel?.();
        resetDrag();
      }}
      style={{
        transform: dragging ? `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0)` : undefined,
      }}
    >
      <FinanceCoin
        name={name}
        formatAmount={formatAmount}
        amount={amount}
        caption={caption}
        color={color}
        icon={icon}
        iconFallback={iconFallback}
        progress={progress}
        danger={danger}
        mutedAmount={mutedAmount}
      />
    </button>
  );
}
